/**
 * Instalación de la PWA. `beforeinstallprompt` se dispara una sola vez y puede llegar antes de que React
 * monte, así que se captura aquí (a nivel de módulo, desde `initPwa`) y los componentes solo lo consultan.
 */

export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

/** Evento no estándar de Chromium. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let notify: ((available: boolean) => void) | null = null;

function onBeforeInstallPrompt(event: Event): void {
  event.preventDefault();
  deferred = event as BeforeInstallPromptEvent;
  notify?.(true);
}

function onAppInstalled(): void {
  deferred = null;
  notify?.(false);
}

/** Escucha el evento de instalación y avisa a `onChange`. Devuelve la función que deja de escuchar. */
export function captureInstallPrompt(onChange: (available: boolean) => void): () => void {
  notify = onChange;
  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.addEventListener('appinstalled', onAppInstalled);
  return () => {
    window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.removeEventListener('appinstalled', onAppInstalled);
    notify = null;
    deferred = null;
  };
}

/** Muestra el diálogo nativo de instalación. El evento solo sirve una vez: después se descarta. */
export async function promptInstall(): Promise<InstallOutcome> {
  const event = deferred;
  if (!event) return 'unavailable';
  deferred = null;
  notify?.(false);
  await event.prompt();
  const { outcome } = await event.userChoice;
  return outcome;
}

const OTHER_IOS_BROWSERS =
  /CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|GSA\/|DuckDuckGo|YaBrowser|FBAN|FBAV|Instagram|Line\/|MicroMessenger/;

/**
 * Safari de iPhone/iPad (incluido iPadOS en "sitio de escritorio"). Solo ahí aplica la instrucción
 * "Compartir → Añadir a la pantalla de inicio"; otros navegadores de iOS y los navegadores integrados
 * de apps no pueden instalarla igual.
 */
export function isIosSafari(userAgent: string, platform: string, maxTouchPoints: number): boolean {
  const isIosDevice =
    /iPad|iPhone|iPod/.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1);
  return (
    isIosDevice &&
    /Version\/[\d.]+.*Safari\//.test(userAgent) &&
    !OTHER_IOS_BROWSERS.test(userAgent)
  );
}

/** ¿La app ya corre instalada (ventana propia)? */
export function isStandalone(): boolean {
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches;
}

export const INSTALL_DISMISS_KEY = 'pizarra:install-dismissed';
export const INSTALL_DISMISS_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

/** ¿El descarte guardado (ms desde epoch, como texto) sigue vigente? Valores raros cuentan como "no descartado". */
export function isDismissalActive(stored: string | null, now: number): boolean {
  if (!stored) return false;
  const at = Number(stored);
  if (!Number.isFinite(at) || at > now) return false;
  return now - at < INSTALL_DISMISS_DAYS * DAY_MS;
}

/** `localStorage` puede faltar o lanzar (modo privado, datos bloqueados): la app debe funcionar igual. */
export function readInstallDismissal(now: number = Date.now()): boolean {
  try {
    return isDismissalActive(window.localStorage.getItem(INSTALL_DISMISS_KEY), now);
  } catch {
    return false;
  }
}

export function rememberInstallDismissal(now: number = Date.now()): void {
  try {
    window.localStorage.setItem(INSTALL_DISMISS_KEY, String(now));
  } catch {
    /* sin almacenamiento: el aviso volverá a aparecer en la próxima visita */
  }
}
